import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, NotebookPen } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useToast } from '../contexts/ToastContext.jsx';
import { t } from '../i18n/nl.js';

function formatDateTime(value) {
  return new Intl.DateTimeFormat('nl-NL', {
    timeZone: 'Europe/Amsterdam', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(new Date(value));
}

function stageLabel(stage) {
  return t(`prospects.stages.${stage || 'nieuwe_lead'}`);
}

function completionLabel(reason) {
  return reason ? t(`prospects.completionReasons.${reason}`) : t('prospects.completionReasons.unknown');
}

export default function ProspectActivityPanel({ clientId }) {
const { showSuccess, showError } = useToast();
  const [pipeline, setPipeline] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [stageSaving, setStageSaving] = useState(false);
  const [showStageModal, setShowStageModal] = useState(false);
  const [activityFilter, setActivityFilter] = useState('all');

  const stages = ['nieuwe_lead', 'in_behandeling', 'kennismaking', 'proeftraining', 'afgerond'];

  const load = useCallback(async () => {
    setLoading(true);
    const [pipelineRes, activityRes] = await Promise.all([
      supabase.from('prospect_pipeline').select('stage, completion_reason, completed_at, next_action_at, next_action_note').eq('prospect_id', clientId).maybeSingle(),
      supabase.from('prospect_activities')
        .select('id, activity_type, body, metadata, created_at, actor:profiles!prospect_activities_actor_id_fkey(full_name)')
        .eq('prospect_id', clientId)
        .order('created_at', { ascending: false })
        .order('id', { ascending: false }),
    ]);
    if (pipelineRes.error || activityRes.error) {
      showError(t('prospects.activitiesError') + (pipelineRes.error || activityRes.error).message);
    }
    setPipeline(pipelineRes.data || { stage: 'nieuwe_lead' });
    setActivities(activityRes.data || []);
    setLoading(false);
  }, [clientId, showError]);

  useEffect(() => { load(); }, [load]);

  async function addNote(event) {
    event.preventDefault();
    if (!note.trim()) return;
    setSaving(true);
    const { error } = await supabase.rpc('add_prospect_note', { p_prospect_id: clientId, p_body: note.trim() });
    setSaving(false);
    if (error) { showError(t('prospects.noteSaveError') + error.message); return; }
    setNote('');
    await load();
    showSuccess(t('prospects.noteSaved'));
  }

  async function changeStage(stage) {
    if (!pipeline || pipeline.stage === stage || stage === 'afgerond') return;
    setStageSaving(true);
    const { data, error } = await supabase.rpc('update_prospect_pipeline', {
      p_prospect_id: clientId,
      p_stage: stage,
      p_update_stage: true,
      p_update_action: false,
      p_completion_reason: null,
    });
    setStageSaving(false);
    if (error) {
      showError(t('prospects.stageSaveError') + error.message);
      return;
    }
    setPipeline(data || { ...pipeline, stage });
    setShowStageModal(false);
    await load();
    showSuccess(t('prospects.stageSaved', { stage: stageLabel(stage) }));
  }

  const currentStageIndex = Math.max(0, stages.indexOf(pipeline?.stage || 'nieuwe_lead'));
  const nextStage = currentStageIndex < stages.length - 2 ? stages[currentStageIndex + 1] : null;
  const visibleActivities = activities.filter(activity => {
    if (activityFilter === 'all') return true;
    if (activityFilter === 'notes') return activity.activity_type === 'note';
    if (activityFilter === 'stage') return activity.activity_type === 'stage_change';
    return true;
  });

  return (
    <section className="card client-prospect-panel client-prospect-workspace">
      <header className="client-prospect-panel-head">
        <div>
          <span>{t('prospects.clientPanelEyebrow')}</span>
          <h2>{stageLabel(pipeline?.stage)}</h2>
          {pipeline?.stage === 'afgerond' && <p>{t('prospects.completedBecause')} {completionLabel(pipeline.completion_reason)}</p>}
        </div>
        <div className="client-prospect-stage-actions">
          <Link to="/coach/prospects" className="btn btn-ghost btn-sm">{t('prospects.openBoard')}<ChevronRight size={14} /></Link>
          {pipeline?.stage !== 'afgerond' && <button className="btn btn-secondary btn-sm" onClick={() => setShowStageModal(true)}>{t('prospects.chooseOtherStage')}</button>}
          {nextStage && <button className="btn btn-primary btn-sm" onClick={() => changeStage(nextStage)} disabled={stageSaving}>{t('prospects.moveToStage', { stage: stageLabel(nextStage) })}</button>}
        </div>
      </header>

      <div className="client-prospect-progress" aria-label={t('prospects.processProgress')}>
        {stages.map((stage, index) => (
          <div key={stage} className={`${index < currentStageIndex ? 'is-done' : ''}${index === currentStageIndex ? ' is-current' : ''}`}>
            <span />
            <small>{stageLabel(stage)}</small>
          </div>
        ))}
      </div>

      <div className="client-prospect-content">
        <div className="client-prospect-main">
          {pipeline?.next_action_note && (
            <div className="client-prospect-next-action">
              <strong>{t('prospects.nextActionTitle')}</strong>
              <span>{pipeline.next_action_note}{pipeline.next_action_at ? ` · ${formatDateTime(pipeline.next_action_at)}` : ''}</span>
            </div>
          )}

          <div className="client-prospect-activity-head">
            <h3>{t('clientActivity.title')}</h3>
            <div>
              {['all', 'notes', 'stage'].map(filter => <button key={filter} className={activityFilter === filter ? 'is-active' : ''} onClick={() => setActivityFilter(filter)}>{t(`prospects.activityFilter.${filter}`)}</button>)}
            </div>
          </div>

          <div className="client-prospect-activities">
            {loading ? <div className="loading-center"><span className="spinner" /></div> : visibleActivities.length === 0 ? (
              <p className="prospect-empty-activity">{t('prospects.noActivities')}</p>
            ) : visibleActivities.map(activity => (
              <article key={activity.id} className={`prospect-activity type-${activity.activity_type}`}>
                <span className="prospect-activity-dot" />
                <div>
                  <strong>{t(`prospects.activity.${activity.activity_type}`)}</strong>
                  {activity.activity_type === 'stage_change' && <p>{stageLabel(activity.metadata?.from)} → {stageLabel(activity.metadata?.to)}{activity.metadata?.completion_reason ? ` · ${completionLabel(activity.metadata.completion_reason)}` : ''}</p>}
                  {activity.body && <p>{activity.body}</p>}
                  <small>{activity.actor?.full_name || t('prospects.systemActor')} · {formatDateTime(activity.created_at)}</small>
                </div>
              </article>
            ))}
          </div>
        </div>

        <form className="client-prospect-note-form" onSubmit={addNote}>
          <label htmlFor="client-prospect-note"><NotebookPen size={16} />{t('prospects.quickNote')}</label>
          <textarea id="client-prospect-note" className="form-input" rows="4" maxLength={4000} value={note} onChange={event => setNote(event.target.value)} placeholder={t('prospects.notePlaceholder')} />
          <small>{t('prospects.noteInternal')}</small>
          <button className="btn btn-primary btn-sm" disabled={saving || !note.trim()}>{saving ? t('common.laden') : t('prospects.addNote')}</button>
        </form>
      </div>

      {showStageModal && (
        <div className="modal-overlay">
          <div className="modal client-stage-modal">
            <div className="modal-header"><h2 className="modal-title">{t('prospects.chooseOtherStage')}</h2><button className="modal-close" onClick={() => setShowStageModal(false)}>✕</button></div>
            <p className="text-muted">{t('prospects.chooseStageHint')}</p>
            <div className="client-stage-options">
              {stages.map(stage => (
                <button key={stage} className={pipeline?.stage === stage ? 'is-current' : ''} onClick={() => stage === 'afgerond' ? null : changeStage(stage)} disabled={stageSaving || pipeline?.stage === stage}>
                  <span>{stageLabel(stage)}</span>
                  <small>{stage === 'afgerond' ? t('prospects.completeOnBoard') : pipeline?.stage === stage ? t('prospects.currentStage') : t('prospects.selectStage')}</small>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
